/**
 * EvidenceProvenance — V7-4-6.
 *
 * Provenance display for evidence / CI check detail views.
 * Shows the provenance level as a colored badge plus emitter identity,
 * CI run link, submission timestamp, and signature verification status.
 */

// ============================================================
// Types
// ============================================================

export interface ProvenanceProps {
  provenance_level: 'unsigned' | 'oidc_verified' | 'sigstore_signed';
  emitter_identity?: string;
  emitter_signature_verified?: boolean;
  ci_run_url?: string;
  submission_timestamp?: string;
}

// ============================================================
// Helpers
// ============================================================

const LEVEL_LABEL: Record<ProvenanceProps['provenance_level'], string> = {
  unsigned: 'Unsigned',
  oidc_verified: 'OIDC Verified',
  sigstore_signed: 'Sigstore Signed',
};

const LEVEL_BG: Record<ProvenanceProps['provenance_level'], string> = {
  unsigned: 'var(--text-dim)',
  oidc_verified: 'var(--success)',
  sigstore_signed: 'var(--accent)',
};

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

// ============================================================
// Sub-components
// ============================================================

/** Colored badge showing the provenance level. */
export function ProvenanceLevelBadge({
  level,
}: {
  level: ProvenanceProps['provenance_level'];
}) {
  return (
    <span
      data-testid="provenance-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: LEVEL_BG[level],
        color: 'var(--text-inverse)',
        letterSpacing: '0.02em',
      }}
    >
      {LEVEL_LABEL[level]}
    </span>
  );
}

/** Verified checkmark icon */
function VerifiedIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-label="Signature verified"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <circle cx="8" cy="8" r="7" stroke="var(--success)" strokeWidth="1.4" fill="none" />
      <path
        d="M5 8l2 2 4-4"
        stroke="var(--success)"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// ============================================================
// Detail display
// ============================================================

export function ProvenanceDetail({
  emitter_identity,
  emitter_signature_verified,
  ci_run_url,
  submission_timestamp,
}: Omit<ProvenanceProps, 'provenance_level'>) {
  const hasAny =
    emitter_identity || ci_run_url || submission_timestamp || emitter_signature_verified;

  if (!hasAny) return null;

  return (
    <div
      data-testid="provenance-detail"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        fontSize: '0.8125rem',
        color: 'var(--text-muted)',
      }}
    >
      {emitter_identity && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'var(--text-dim)' }}>Emitter:</span>
          <span style={{ color: 'var(--text)' }}>{emitter_identity}</span>
          {emitter_signature_verified && <VerifiedIcon />}
        </div>
      )}

      {!emitter_identity && emitter_signature_verified && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <VerifiedIcon />
          <span style={{ color: 'var(--text)' }}>Signature verified</span>
        </div>
      )}

      {ci_run_url && (
        <div>
          <span style={{ color: 'var(--text-dim)' }}>CI run: </span>
          <a
            href={ci_run_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent)', textDecoration: 'underline' }}
            data-testid="ci-run-link"
          >
            {ci_run_url}
          </a>
        </div>
      )}

      {submission_timestamp && (
        <div>
          <span style={{ color: 'var(--text-dim)' }}>Submitted: </span>
          <span style={{ color: 'var(--text)' }}>
            {formatTimestamp(submission_timestamp)}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Combined component
// ============================================================

export function EvidenceProvenance(props: ProvenanceProps) {
  const {
    provenance_level,
    emitter_identity,
    emitter_signature_verified,
    ci_run_url,
    submission_timestamp,
  } = props;

  return (
    <div
      data-testid="evidence-provenance"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '12px',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        background: 'var(--surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'var(--text)',
          }}
        >
          Provenance
        </span>
        <ProvenanceLevelBadge level={provenance_level} />
      </div>

      <ProvenanceDetail
        emitter_identity={emitter_identity}
        emitter_signature_verified={emitter_signature_verified}
        ci_run_url={ci_run_url}
        submission_timestamp={submission_timestamp}
      />
    </div>
  );
}
